<script setup lang="ts">
import { onMounted, ref, computed, defineComponent, createCommentVNode } from 'vue'
import { useI18n } from 'vue-i18n'

import { AddJavascriptToHeader, SetIsLoading } from '@/services/AoeHelpers'
import { CartPaymentTokenCreate } from '@/api/Cart'
import { ApiPost } from '@/api/Common'
import { GetImageRootUrl, GetOrderEntryConnectorUrl } from '@/stores/SettingsStore'
import type { IAoeCart } from '@/types/CartTypes'
import type { IBraintree } from '@/types/PaymentGatewayTypes'
import type { AxiosError } from 'axios'
import { useLogger } from 'vue-logger-plugin'

declare const braintree: IBraintree

type ApplePayTokenizePayload = Record<string, unknown>

const { t } = useI18n()
const logger = useLogger()

const props = defineProps<{
  cart: IAoeCart
  systemKeyword: string
  parameters: {
    braintreeMerchantId: string
    displayName: string
  }
}>()

logger.debug('BraintreeApplePayPaymentEntry', props)

const isSupported = ref(false)
const isInitializing = ref(false)
const initError = ref('')
const applePayCheckoutImageUrl = ref('')

const canRender = computed(() => isSupported.value && !initError.value)

const BraintreeApplePayNotRenderedComment = defineComponent({
  name: 'BraintreeApplePayNotRenderedComment',
  props: {
    isSupported: { type: Boolean, required: true },
    isInitializing: { type: Boolean, required: true },
    initError: { type: String, required: true },
  },
  setup(props) {
    let hasLogged = false
    return () => {
      const message = `BraintreeApplePayPaymentEntry not rendered: canRender=false, isSupported=${String(
        props.isSupported,
      )}, isInitializing=${String(props.isInitializing)}, initError=${String(
        props.initError,
      )}`

      if (!hasLogged) {
        hasLogged = true
        logger.debug('BraintreeApplePayPaymentEntry', message)
      }

      return createCommentVNode(message)
    }
  },
})

let applePayInstance: any | null = null
let clientToken: string | null = null

function postPaymentError(error: unknown) {
  const axiosError = error as AxiosError
  const messageData = axiosError?.response?.data ?? null
  window.postMessage({ messageType: 'aoepaymentcreationerror', messageData })
}

async function createPayment(
  payload: ApplePayTokenizePayload,
  billingContact?: unknown,
  shippingContact?: unknown,
) {
  const createPaymentUrl = await GetOrderEntryConnectorUrl('CartPaymentCreate')

  const braintreeApplePayTokenizeResponse: Record<string, unknown> = {
    ...payload,
    clientToken,
  }

  if (billingContact !== undefined || shippingContact !== undefined) {
    const payment: Record<string, unknown> = {}

    if (billingContact !== undefined) {
      payment.billingContact = billingContact
    }

    if (shippingContact !== undefined) {
      payment.shippingContact = shippingContact
    }

    braintreeApplePayTokenizeResponse.payment = payment
  }

  const jsonDataToSave = {
    systemKeyword: props.systemKeyword,
    aOECartIdToken: props.cart.aoeCartIdToken,
    BraintreeApplePayTokenizeResponse: braintreeApplePayTokenizeResponse,
    charge: Number(props.cart.balance),
  }

  return ApiPost(createPaymentUrl, jsonDataToSave)
    .then((result: unknown) => {
      window.postMessage({ messageType: 'aoepaymentcreated', messageData: result })
    })
    .catch((error: unknown) => {
      postPaymentError(error)
    })
}

async function startApplePaySession() {
  if (!applePayInstance || !clientToken) return

  const ApplePaySessionCtor = (window as any).ApplePaySession
  if (!ApplePaySessionCtor) return

  SetIsLoading(true)

  try {
    const paymentRequest = applePayInstance.createPaymentRequest({
      total: {
        label: props.parameters.displayName,
        amount: props.cart.balance.toFixed(2),
      },
      requiredBillingContactFields: ['postalAddress', 'name', 'email', 'phone'],
    })

    const session = new ApplePaySessionCtor(3, paymentRequest)

    session.onvalidatemerchant = (event: any) => {
      applePayInstance.performValidation(
        {
          validationURL: event.validationURL,
          displayName: props.parameters.displayName,
        },
        (err: any, merchantSession: any) => {
          if (err) {
            logger.error(
              'BraintreeApplePayPaymentEntry',
              'Apple Pay merchant validation failed',
              event.validationURL,
              props.parameters.displayName,
              err,
            )
            session.abort()
            postPaymentError(err)
            SetIsLoading(false)
            return
          }
          session.completeMerchantValidation(merchantSession)
        },
      )
    }

    session.onpaymentauthorized = (event: any) => {
      applePayInstance.tokenize(
        {
          token: event.payment.token,
        },
        async (err: any, payload: any) => {
          if (err) {
            logger.error(
              'BraintreeApplePayPaymentEntry',
              'Apple Pay tokenization failed',
              err,
            )
            session.completePayment(ApplePaySessionCtor.STATUS_FAILURE)
            postPaymentError(err)
            SetIsLoading(false)
            return
          }

          await createPayment(
            payload as ApplePayTokenizePayload,
            event.payment.billingContact,
            event.payment.shippingContact,
          )
          session.completePayment(ApplePaySessionCtor.STATUS_SUCCESS)
          SetIsLoading(false)
        },
      )
    }

    session.oncancel = () => {
      SetIsLoading(false)
    }

    session.begin()
  } catch (error) {
    logger.error('BraintreeApplePayPaymentEntry', 'Apple Pay session failed', error)
    postPaymentError(error)
    SetIsLoading(false)
  }
}

onMounted(async () => {
  isInitializing.value = true

  try {
    if (typeof window === 'undefined') return

    const imageRootUrl = await GetImageRootUrl()
    applePayCheckoutImageUrl.value =
      imageRootUrl + '/contentimages/Apple_Pay_Mark_RGB_041619.svg'

    const ApplePaySessionCtor = (window as any).ApplePaySession
    isSupported.value =
      !!ApplePaySessionCtor &&
      typeof ApplePaySessionCtor.canMakePayments === 'function' &&
      !!ApplePaySessionCtor.canMakePayments()

    if (!isSupported.value) return

    await Promise.all([
      AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/client.min.js'),
      AddJavascriptToHeader('https://js.braintreegateway.com/web/3.129.0/js/apple-pay.min.js'),
    ])

    logger.debug('BraintreeApplePayPaymentEntry', 'CartPaymentTokenCreate: start')
    const response = await CartPaymentTokenCreate({
      aoeCartIdToken: props.cart.aoeCartIdToken,
      systemKeyword: props.systemKeyword,
      charge: props.cart.balance,
      tokenType: 'applepay'
    })
    logger.debug('BraintreeApplePayPaymentEntry', 'CartPaymentTokenCreate: done', response)

    if (!response?.item?.ClientToken) {
      initError.value = t('cart.NotSupported')
      return
    }

    clientToken = response.item.ClientToken

    await new Promise<void>((resolve, reject) => {
      braintree.client.create(
        { authorization: clientToken },
        (clientErr: any, clientInstance: any) => {
          if (clientErr) {
            reject(clientErr)
            return
          }

          ;(braintree as any).applePay.create(
            {
              client: clientInstance,
              merchantIdentifier: props.parameters.braintreeMerchantId,
            },
            (applePayErr: any, instance: any) => {
              if (applePayErr) {
                reject(applePayErr)
                return
              }
              applePayInstance = instance
              resolve()
            },
          )
        },
      )
    })
  } catch (error) {
    logger.error('BraintreeApplePayPaymentEntry', 'Braintree Apple Pay init failed', error)
    initError.value = t('cart.NotSupported')
  } finally {
    isInitializing.value = false
  }
})
</script>

<template>
  <div v-if="canRender" class="apple-pay-entry">
    <button
      type="button"
      class="applePayButton"
      @click="startApplePaySession"
      data-testid="braintree-applepay-btn"
    >
      <span class="applePayContent">
        <span class="applePayLabel">
          {{ t('cart.ApplePayCheckout') }}
        </span>
        <img
          class="applePayImage"
          :src="applePayCheckoutImageUrl"
          :alt="t('cart.ApplePayImageAlt')"
        />
      </span>
    </button>
  </div>
  <template v-else>
    <BraintreeApplePayNotRenderedComment
      :isSupported="isSupported"
      :isInitializing="isInitializing"
      :initError="initError"
    />
  </template>
</template>

<style lang="scss" scoped>
.applePayButton {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  width: 100%;
  padding: $spacing-4 $spacing-12;
  border-radius: $radius-md;
  border: 1px solid var(--color-text);
  background: var(--color-text);
  color: var(--color-background);
  text-transform: uppercase;
  line-height: 1;
}

.applePayContent {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $gap-sm;
  max-width: 100%;
}

.applePayLabel {
  flex: 0 0 auto;
  white-space: nowrap;
}

.applePayImage {
  display: block;
  flex: 0 0 auto;
  width: auto;
  height: $spacing-6;
  max-height: 24px;
  max-width: 100%;
  object-fit: contain;
}
</style>
